import React, { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Api } from "../Api/Api";

const TodayPending = () => {
  const navigate = useNavigate();
  const [pendingList, setPendingList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [type, setType] = useState("all");
  const [types, setTypes] = useState([]);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [custRes, payRes] = await Promise.all([
        fetch(`${Api}customers/list.php`),
        fetch(`${Api}payments/list.php`),
      ]);

      const custData = await custRes.json();
      const payData = await payRes.json();

      const customers = custData.customers || [];
      const payments = payData.payments || [];

      const yesterday = new Date();
      yesterday.setDate(yesterday.getDate() - 1);

      const yYear = yesterday.getFullYear();
      const yMonth = String(yesterday.getMonth() + 1).padStart(2, "0");
      const yDay = String(yesterday.getDate()).padStart(2, "0");
      const yesterdayStr = `${yYear}-${yMonth}-${yDay}`;

      const yesterdayStart = new Date(yesterdayStr);
      yesterdayStart.setHours(0, 0, 0, 0);

      const pending = [];

      customers
        .filter((c) => c.status === "Active")
        .forEach((c) => {
          if (!c.given_date) return;

          const given = new Date(c.given_date + "T00:00:00");
          const diffDays = Math.floor((yesterdayStart - given) / 86400000);

          if (diffDays >= 7 && diffDays % 7 === 0) {
            const weekNum = diffDays / 7;

            const customerPayments = payments.filter(
              (p) => Number(p.customer_id) === Number(c.id),
            );

            const paidForWeek = customerPayments.some(
              (p) => Number(p.week_number) === weekNum,
            );

            if (!paidForWeek) {
              pending.push({
                ...c,
                dueWeek: weekNum,
                dueDate: yesterdayStr,
              });
            }
          }
        });

      setPendingList(pending);

      const typeRes = await fetch(`${Api}customers/types/list.php`);
      const typeData = await typeRes.json();
      setTypes(typeData.types || []);
    } catch (err) {
      console.error("TodayPending Error:", err);
    }
    setLoading(false);
  };

  const filteredList = useMemo(() => {
    const q = search.toLowerCase();
    return pendingList.filter((c) => {
      const matchesSearch =
        !search ||
        c.name?.toLowerCase().includes(q) ||
        c.customer_code?.toLowerCase().includes(q) ||
        c.mobile1?.includes(search);
      const matchesType = type === "all" || c.type === type;
      return matchesSearch && matchesType;
    });
  }, [pendingList, search, type]);

  const formatDateDMY = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6 font-sans">
      <div className="max-w-6xl mx-auto space-y-6">
        <div className="flex justify-between items-center">
          <h1 className="text-2xl font-black text-gray-900 tracking-tight">
            TODAY PENDING
          </h1>
          <div className="text-sm text-gray-500 font-medium">
            Pending count: {filteredList.length}
          </div>
        </div>

        {loading ? (
          <div className="text-center py-12 text-gray-400">Loading...</div>
        ) : pendingList.length === 0 ? (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
            <p className="text-gray-400 font-medium italic">
              No pending payments from yesterday.
            </p>
          </div>
        ) : (
          <>
          <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-200 flex flex-wrap items-center gap-4">
            <input
              type="text"
              placeholder="Search by Name, Code or Mobile..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="flex-1 min-w-[200px] px-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:outline-none"
            />
            <select
              value={type}
              onChange={(e) => setType(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm font-bold bg-white"
            >
              <option value="all">All Types</option>
              {types.map((t) => (
                <option key={t.id} value={t.type_name}>{t.type_name}</option>
              ))}
            </select>
            <span className="text-sm text-gray-500 font-medium">
              {filteredList.length} results
            </span>
          </div>
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-x-auto">
            <table className="min-w-[900px] w-full text-sm text-left">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-4 font-bold text-gray-700 uppercase text-xs">#</th>
                  <th className="px-6 py-4 font-bold text-gray-700 uppercase text-xs">Customer Code</th>
                  <th className="px-6 py-4 font-bold text-gray-700 uppercase text-xs">Name</th>
                  <th className="px-6 py-4 font-bold text-gray-700 uppercase text-xs">Mobile</th>
                  <th className="px-6 py-4 font-bold text-gray-700 uppercase text-xs">Loan Date</th>
                  <th className="px-6 py-4 font-bold text-gray-700 uppercase text-xs">Loan Amount</th>
                  <th className="px-6 py-4 font-bold text-gray-700 uppercase text-xs">Weekly Amount</th>
                  <th className="px-6 py-4 font-bold text-gray-700 uppercase text-xs">Due Date</th>
                  <th className="px-6 py-4 font-bold text-gray-700 uppercase text-xs text-center">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filteredList.length === 0 ? (
                  <tr>
                    <td colSpan={9} className="text-center py-12 text-gray-400 italic">
                      No matching records.
                    </td>
                  </tr>
                ) : filteredList.map((c, i) => (
                  <tr key={c.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4 text-gray-400">{i + 1}</td>
                    <td className="px-6 py-4 font-mono text-indigo-600 font-semibold">
                      {c.customer_code}
                    </td>
                    <td className="px-6 py-4 font-semibold text-gray-800">
                      {c.name}
                    </td>
                    <td className="px-6 py-4 text-gray-600">
                      {c.mobile1 || "-"}
                    </td>
                    <td className="px-6 py-4 text-gray-500">
                      {formatDateDMY(c.given_date)}
                    </td>
                    <td className="px-6 py-4 text-gray-800 font-semibold">
                      ₹{Number(c.investment_amount || 0).toLocaleString()}
                    </td>
                    <td className="px-6 py-4 text-gray-800 font-semibold">
                      ₹{Number(c.weekly_interest_amount || 0).toLocaleString()}
                    </td>
                    <td className="px-6 py-4 text-red-600 font-semibold">
                      {formatDateDMY(c.dueDate)}
                    </td>
                    <td className="px-6 py-4 text-center">
                      <button
                        onClick={() => navigate(`/admin/customers/${c.id}`, { state: { from: "today-pending" } })}
                        className="px-4 py-1.5 rounded-md text-xs font-bold bg-indigo-600 text-white hover:bg-indigo-700 transition-colors"
                      >
                        View
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          </>
        )}
      </div>
    </div>
  );
};

export default TodayPending;
